import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Sparkles, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Renders a "Request coaching" CTA for end users whose client_type is
 * "prospect" or "trainee". Hides itself otherwise (e.g. coachees already
 * have an active coaching relationship).
 */
export default function RequestCoachingButton({ clientType }) {
  const { t } = useT();
  const eligible = clientType === "prospect" || clientType === "trainee";
  const [latest, setLatest] = useState(null); // {status, created_at, ...} | null
  const [sending, setSending] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!eligible) {
      setLoaded(true);
      return;
    }
    const load = async () => {
      try {
        const { data } = await api.get("/qol/me/coaching-request");
        setLatest(data); // null when none
      } catch (e) {
        // Non-critical: the CTA still renders in its default "no pending
        // request" state if the lookup fails — user can still send a fresh
        // request and the backend de-dupes on its side.
      } finally {
        setLoaded(true);
      }
    };
    load();
  }, [eligible]);

  if (!eligible || !loaded) return null;

  const submit = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/qol/me/request-coaching");
      setLatest(data);
      toast.success(t("request_coaching.toast.success"));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("errors.generic"));
    } finally {
      setSending(false);
    }
  };

  const hasPending = latest && latest.status === "pending";

  return (
    <div
      className="bg-[#F2F0EA] border border-[#E5E2DC] rounded-2xl p-6 sm:p-8 mt-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
      data-testid="request-coaching-card"
    >
      <div className="flex items-start gap-4">
        <div className="h-11 w-11 rounded-xl bg-white flex items-center justify-center shrink-0 border border-[#E5E2DC]">
          <Sparkles strokeWidth={1.5} className="text-[#B37A5F]" size={20} />
        </div>
        <div>
          <h3 className="font-heading text-xl text-[#1A1D1A] tracking-tight font-semibold">
            {t("request_coaching.title")}
          </h3>
          <p className="text-sm text-[#5C635A] mt-1 leading-relaxed max-w-xl">
            {hasPending ? t("request_coaching.pending") : t("request_coaching.idle")}
          </p>
        </div>
      </div>
      <div className="flex flex-col items-end gap-2 shrink-0">
        <Button
          onClick={submit}
          disabled={sending}
          data-testid="request-coaching-button"
          className="h-11 rounded-lg bg-[#4A5D4E] hover:bg-[#3D4D41] text-white"
        >
          {hasPending ? (
            <>
              <CheckCircle2 strokeWidth={1.5} size={16} className="mr-2" />
              {sending ? t("request_coaching.sending") : t("request_coaching.send_again")}
            </>
          ) : (
            <>
              <Sparkles strokeWidth={1.5} size={16} className="mr-2" />
              {sending ? t("request_coaching.sending") : t("request_coaching.cta")}
            </>
          )}
        </Button>
        {hasPending && (
          <p className="text-[10px] uppercase tracking-[0.2em] text-[#8A9188] font-bold" data-testid="request-coaching-status">
            {t("request_coaching.pending_status", { date: new Date(latest.created_at).toLocaleDateString() })}
          </p>
        )}
      </div>
    </div>
  );
}
